import React, { useState } from 'react';
import {
  ArrowLeftIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { BottomSheet } from '../ui/BottomSheet';
import PermissionGate from '../PermissionGate';
import {
  avatarTone,
  formatPlanPrice,
  planActiveChipClass,
  planInitials,
  planTypeChipClass,
  planTypeLabel,
} from './planTemplateUtils';
import type { PlanTemplateCardData } from './PlanTemplateMobileCard';

export interface PlanTemplateMobileDetailProps {
  template: PlanTemplateCardData & {
    description?: string;
    createdAt: string;
    updatedAt: string;
  };
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export const PlanTemplateMobileDetail: React.FC<PlanTemplateMobileDetailProps> = ({
  template,
  onBack,
  onEdit,
  onDelete,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const items = template.features?.length ?? 0;

  const run = (fn: () => void) => {
    setMoreOpen(false);
    fn();
  };

  return (
    <div className="animate-fade-in-up space-y-4 pb-8 md:hidden">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 active:scale-95"
          aria-label="Volver"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 active:scale-95"
          aria-label="Más acciones"
        >
          <EllipsisVerticalIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-col items-center text-center">
        <div
          className={clsx(
            'flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold ring-2 ring-white',
            avatarTone(`${template.name}${template.id}`)
          )}
        >
          {planInitials(template.name)}
        </div>
        <h1 className="mt-3 text-xl font-bold text-slate-900">{template.name}</h1>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <span className={planActiveChipClass(template.isActive)}>
            {template.isActive ? 'Activo' : 'Inactivo'}
          </span>
          <span
            className={clsx(
              'inline-flex rounded-lg px-2 py-0.5 text-[11px] font-semibold',
              planTypeChipClass(template.planType)
            )}
          >
            {planTypeLabel(template.planType)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 rounded-2xl border border-[#E5EAF1] bg-white px-2 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Precio base
          </p>
          <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">
            {formatPlanPrice(template.basePrice)}
          </p>
        </div>
        <div className="border-x border-[#E5EAF1] text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Máx. familiares
          </p>
          <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">
            {template.maxFamilyMembers}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Ítems</p>
          <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">{items}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E5EAF1] bg-white">
        <DetailRow label="Tipo" value={planTypeLabel(template.planType)} />
        <DetailRow label="Máx. familiares" value={String(template.maxFamilyMembers)} />
        <DetailRow label="Precio base" value={formatPlanPrice(template.basePrice)} />
        <DetailRow
          label="Características"
          value={items ? `${items} ítem${items === 1 ? '' : 's'}` : 'Ninguna'}
        />
        <DetailRow
          label="Descripción"
          value={template.description?.trim() || 'Sin descripción'}
          multiline
        />
        <DetailRow
          label="Cobertura familiar"
          value={
            items
              ? template.features.join(' · ')
              : `Hasta ${template.maxFamilyMembers} familiares incluidos en la cobertura del plan.`
          }
          multiline
        />
        <DetailRow label="Fecha de creación" value={formatDate(template.createdAt)} />
        <DetailRow
          label="Última actualización"
          value={formatDate(template.updatedAt)}
          last
        />
      </div>

      <div className="flex items-center justify-center gap-6 pt-2">
        <PermissionGate permission="plan_templates.update">
          <button
            type="button"
            onClick={onEdit}
            className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-600 transition active:scale-95"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm">
              <PencilIcon className="h-5 w-5 text-primary-600" />
            </span>
            Editar
          </button>
        </PermissionGate>
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-600 transition active:scale-95"
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm">
            <EyeIcon className="h-5 w-5 text-primary-600" />
          </span>
          Ver detalles
        </button>
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-600 transition active:scale-95"
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm">
            <EllipsisVerticalIcon className="h-5 w-5 text-slate-600" />
          </span>
          Más
        </button>
      </div>

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Más acciones">
        <div className="space-y-0.5 pb-2">
          <PermissionGate permission="plan_templates.update">
            <button
              type="button"
              onClick={() => run(onEdit)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
            >
              <PencilIcon className="h-5 w-5 text-slate-400" />
              Editar plantilla
            </button>
          </PermissionGate>
          <PermissionGate permission="plan_templates.delete">
            <button
              type="button"
              onClick={() => run(onDelete)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-rose-600 hover:bg-rose-50"
            >
              <TrashIcon className="h-5 w-5 text-rose-500" />
              Eliminar
            </button>
          </PermissionGate>
        </div>
      </BottomSheet>
    </div>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: string;
  multiline?: boolean;
  last?: boolean;
}> = ({ label, value, multiline, last }) => (
  <div
    className={clsx(
      'px-4 py-3',
      !last && 'border-b border-[#E5EAF1]',
      multiline ? 'space-y-1' : 'flex items-start justify-between gap-3'
    )}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p
      className={clsx(
        'text-sm font-medium text-slate-900',
        !multiline && 'text-right',
        multiline && 'leading-relaxed text-slate-700'
      )}
    >
      {value}
    </p>
  </div>
);

export default PlanTemplateMobileDetail;
